/**
 * Quick 260813-iqd — "Personal email" ProfilePage card.
 *
 * Lets the user see, set, and clear their own personal email — the private
 * address Monk uses to match external accounts (e.g. a LinkedIn account
 * signed up under a personal address) to their Monksheets identity. Without
 * it, a LinkedIn OneCLI connection whose LinkedIn email isn't the work
 * address can never auto-grant.
 *
 * Visual language matches ProfilePage's other cards (bg-white rounded-xl
 * shadow-sm ...), NOT the ui/ kit used by PreferencesPage. Explicit
 * Save/Clear buttons rather than save-on-blur — this is an identity field
 * on a page with no other autosave affordance.
 *
 * On success the component calls refreshUser() so GET /api/auth/me
 * re-populates authContext.user.personalEmail (same pattern as
 * PhoneNumberField). Clearing sends `null` — the backend rejects '' (D-04).
 */
import { useState } from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { useAuth } from '../../services/authContext';
import { useUpdatePersonalEmail } from '../../hooks/usePersonalEmail';

// Frontend-side friendliness check only — the backend re-validates with
// Fastify `format: 'email'` + Zod `.email()` (defense-in-depth).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PersonalEmailSection() {
  const { user, refreshUser } = useAuth();
  const updateEmail = useUpdatePersonalEmail();

  const storedValue = user?.personalEmail ?? null;
  const [value, setValue] = useState(storedValue ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSave = () => {
    const trimmed = value.trim();
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setValidationError('Enter a valid email address (e.g. name@gmail.com).');
      return;
    }
    setValidationError(null);
    updateEmail.mutate(trimmed, {
      onSuccess: () => {
        // Re-fetch /api/auth/me so authContext.user.personalEmail updates.
        void refreshUser();
      },
    });
  };

  const handleClear = () => {
    setValue('');
    setValidationError(null);
    updateEmail.mutate(null, {
      onSuccess: () => {
        void refreshUser();
      },
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <EnvelopeIcon className="h-6 w-6 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Personal email</h2>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Your private address, used to match external accounts connected through Monk — for
        example a LinkedIn account signed up under a personal address. Visible only to you and
        to admins.
      </p>

      <div className="flex items-center gap-2">
        <input
          type="email"
          aria-label="Personal email address"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setValidationError(null);
          }}
          placeholder="name@gmail.com"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <Button onClick={handleSave} disabled={updateEmail.isPending}>
          Save
        </Button>
        {storedValue && (
          <Button variant="secondary" onClick={handleClear} disabled={updateEmail.isPending}>
            Clear
          </Button>
        )}
      </div>

      {validationError && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {validationError}
        </p>
      )}
    </div>
  );
}
